import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const STAGE_OPTIONS = [
  { key: "TOF", label: "Top of Funnel" },
  { key: "MOF", label: "Middle of Funnel" },
  { key: "BOF", label: "Bottom of Funnel" },
  { key: "RETARGETING", label: "Retargeting" },
  { key: "COMPETITOR", label: "Competitor" },
] as const;

export interface StagePickerProps {
  availableStages: Record<string, number>;
  // Submits a synthesized chat message (e.g. "make TOF and MOF ads from my angles")
  // through the parent chat handler. Parent typically forwards to /api/canvas/agent.
  onSubmit: (message: string) => void;
  onCancel?: () => void;
}

function buildMessage(selected: string[]): string {
  if (selected.length === 0) return "";
  if (selected.length === 1) return `make ${selected[0]} ads from my angles`;
  if (selected.length === STAGE_OPTIONS.length) return "build a full campaign from my angles";
  // Multi-stage: "make TOF and MOF ads from my angles" or "make TOF, MOF, and BOF ads from my angles"
  const head = selected.slice(0, -1).join(", ");
  const tail = selected[selected.length - 1];
  return `make ${head} and ${tail} ads from my angles`;
}

export function StagePicker({ availableStages, onSubmit, onCancel }: StagePickerProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const orderedSelection = STAGE_OPTIONS.map(s => s.key).filter(k => selected.has(k));

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3" data-testid="stage-picker">
      <div className="flex flex-wrap gap-2">
        {STAGE_OPTIONS.map((s) => {
          const count = availableStages[s.key] ?? 0;
          const isDisabled = count === 0;
          const isSelected = selected.has(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => !isDisabled && toggle(s.key)}
              disabled={isDisabled}
              data-testid={`stage-chip-${s.key}`}
              aria-pressed={isSelected}
              className={[
                "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                isSelected
                  ? "bg-primary text-primary-foreground border-primary"
                  : isDisabled
                  ? "bg-muted/40 text-muted-foreground border-transparent cursor-not-allowed opacity-60"
                  : "bg-background hover:bg-muted border-border",
              ].join(" ")}
              title={isDisabled ? `No ${s.key} angles yet — regenerate that stage first` : undefined}
            >
              {s.label}
              <Badge variant={isSelected ? "secondary" : "outline"} className="text-[10px] px-1.5">
                {count}
              </Badge>
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-end gap-2">
        {onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel} data-testid="stage-picker-cancel">
            Cancel
          </Button>
        )}
        <Button
          size="sm"
          disabled={orderedSelection.length === 0}
          onClick={() => onSubmit(buildMessage(orderedSelection))}
          data-testid="stage-picker-submit"
        >
          Generate ads
          {orderedSelection.length > 0 && <span className="ml-1 opacity-75">({orderedSelection.length})</span>}
        </Button>
      </div>
    </div>
  );
}
